import { GET, POST } from "../config/axios.config";
import { normalizeOrder, normalizeWishOrder, normalizeWishOrders } from "./normalizers";

/**
 * v2 moved wish orders onto their own `/wish-orders` resource with a proper
 * offer log: the customer opens with a price, each side counters via
 * `/offer`, and either side can `/accept` or `/reject`. An accepted wish is
 * checked out through `/order`, which produces a normal order.
 */

export const listMyWishesPage = async ({ page = 1, limit = 20, status, search } = {}) => {
  const res = await GET("/wish-orders", {
    params: {
      page,
      limit,
      ...(status ? { status } : {}),
      ...(search ? { search } : {}),
    },
  });
  const pagination = res?.pagination || { total: 0, page, limit, totalPages: 1 };
  return {
    items: normalizeWishOrders(res?.data),
    pagination,
    hasMore: pagination.page < pagination.totalPages,
    nextPage: pagination.page + 1,
  };
};

export const listMyWishes = async (options) =>
  (await listMyWishesPage(options)).items;

export const getWishById = async (wishId) => {
  const res = await GET(`/wish-orders/${wishId}`);
  return normalizeWishOrder(res?.data?.wishOrder);
};

/**
 * @param {object} input
 * @param {string} input.desiredDate  YYYY-MM-DD — the API rejects a datetime.
 * @param {string} input.timeSlot     free-text slot, e.g. "13:30" or "Evening".
 * @param {number} input.offerPrice   the customer's opening offer.
 */
export const createWish = async ({
  chefId,
  dishName,
  quantity,
  unit,
  desiredDate,
  timeSlot,
  ingredients = [],
  dietaryRestrictions = [],
  notes,
  referencePhotos = [],
  referenceLink,
  offerPrice,
}) => {
  const res = await POST("/wish-orders", {
    chefId,
    dishName,
    quantity: Number(quantity),
    unit,
    // Trim any time component the date input may have carried.
    desiredDate: String(desiredDate).slice(0, 10),
    timeSlot,
    ingredients,
    dietaryRestrictions,
    ...(notes ? { notes } : {}),
    referencePhotos,
    ...(referenceLink ? { referenceLink } : {}),
    offerPrice: Number(offerPrice),
  });
  return normalizeWishOrder(res?.data?.wishOrder);
};

/** Counter-offer from the customer. */
export const sendOffer = async (wishId, amount) => {
  const res = await POST(`/wish-orders/${wishId}/offer`, {
    amount: Number(amount),
  });
  return normalizeWishOrder(res?.data?.wishOrder);
};

export const acceptWish = async (wishId) => {
  const res = await POST(`/wish-orders/${wishId}/accept`);
  return normalizeWishOrder(res?.data?.wishOrder);
};

export const rejectWish = async (wishId) => {
  const res = await POST(`/wish-orders/${wishId}/reject`);
  return normalizeWishOrder(res?.data?.wishOrder);
};

/** Checkout of an accepted wish. Safe to retry — returns the same order. */
export const orderFromWish = async (wishId, { addressId, paymentMethod = "cod" }) => {
  const res = await POST(`/wish-orders/${wishId}/order`, {
    addressId,
    paymentMethod,
  });
  return normalizeOrder(res?.data?.order);
};
